import { useMutation, useQueryClient } from "@tanstack/react-query";
import rg4js from "raygun4js";
import { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
  CommunicationLanguage,
  CommunicationStatus,
  ObjectTypeEnum,
  type ViewCommunication,
  type ViewCommunicationItem,
} from "@/briefpoint-client";
import { toast } from "@/components/ui/toast";
import { useCommunicationApi } from "@/hooks/useApi";
import { getCommunicationsForObjectOptions } from "@/lib/query-options";
import { normalizeError } from "@/lib/utils";
import type { ReviewClientTasksPageData } from "./useReviewClientTasksPageData";

/**
 * Add-task URL, add/edit/delete task mutations, delete modal, and Next handoff.
 * Composed with `useReviewClientTasksPageData` in the route; derived view flags
 * (e.g. unassigned / next row visibility) live in the screen component.
 */
export function useReviewClientTasksPageActions(
  pageData: ReviewClientTasksPageData,
) {
  const history = useHistory();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const queryClient = useQueryClient();
  const communicationApi = useCommunicationApi();

  const [deleteCommunication, setDeleteCommunication] = useState<
    ViewCommunicationItem | undefined
  >(undefined);
  const [editCommunication, setEditCommunication] = useState<
    ViewCommunicationItem | undefined
  >(undefined);

  function addTask() {
    if (pageData.isReviewLocked) return;
    setEditCommunication(undefined);
    searchParams.set("add_task", "true");
    history.push({
      pathname: location.pathname,
      search: searchParams.toString(),
    });
  }

  const deleteCommunicationMutation = useMutation({
    mutationFn: async (id: string | undefined) => {
      return communicationApi.communicationDeleteCommunicationItem({
        communicationId: pageData.activeCommunication?.id ?? "",
        communicationItemId: deleteCommunication?.id ?? id ?? "",
      });
    },
    onError: (error, _, context) => {
      if (context?.previousData) {
        const queryKey = getCommunicationsForObjectOptions(
          communicationApi,
          pageData.documentId,
          ObjectTypeEnum.Document,
        ).queryKey;
        queryClient.setQueryData(queryKey, context.previousData);
      }
      toast.error("Failed to delete task. Please try again.");
      rg4js("send", {
        error: normalizeError(error),
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: getCommunicationsForObjectOptions(
          communicationApi,
          pageData.documentId,
          ObjectTypeEnum.Document,
        ).queryKey,
      });
      setDeleteCommunication(undefined);
    },
    onMutate: async (id: string | undefined) => {
      if (!id) return;
      const queryKey = getCommunicationsForObjectOptions(
        communicationApi,
        pageData.documentId,
        ObjectTypeEnum.Document,
      ).queryKey;
      await queryClient.cancelQueries({ queryKey });
      const previousData =
        queryClient.getQueryData<ViewCommunication[]>(queryKey);
      queryClient.setQueryData<ViewCommunication[]>(queryKey, (old) => {
        if (!old) return old;
        return old.map((communication) => {
          if (
            communication.externalCommunicationItems?.some(
              (item) => item.id === id,
            )
          ) {
            return {
              ...communication,
              externalCommunicationItems:
                communication.externalCommunicationItems.filter(
                  (item) => item.id !== id,
                ),
            };
          }
          return communication;
        });
      });
      return { previousData };
    },
  });

  const deleteInterrogatoryMutation = useMutation({
    mutationFn: async ({
      communicationId,
      interrogatoryId,
    }: {
      communicationId: string;
      interrogatoryId: number;
    }) => {
      const communicationText =
        pageData.activeCommunication?.externalCommunicationItems?.find(
          (c) => c.id === communicationId,
        )?.communication;
      const removedAssociatedObjectIds =
        pageData.activeCommunication?.externalCommunicationItems
          ?.find((c) => c.id === communicationId)
          ?.responseInfo?.associatedObjectIds?.filter(
            (id) => Number(id) !== interrogatoryId,
          );
      return communicationApi.communicationPutCommunicationItem({
        id: pageData.activeCommunication?.id ?? "",
        putCommunicationItem: {
          id: communicationId,
          objectType: ObjectTypeEnum.Interrogatory,
          objectId: interrogatoryId.toString(),
          communication: communicationText ?? "",
          language: CommunicationLanguage.EN,
          responseInfo: {
            associatedObjectIds: removedAssociatedObjectIds?.map((id) =>
              id.toString(),
            ),
          },
        },
      });
    },
    onSuccess: () => {
      toast.success("Task deleted successfully");
    },
    onError: (error, _, context) => {
      if (context?.previousData) {
        const queryKey = getCommunicationsForObjectOptions(
          communicationApi,
          pageData.documentId,
          ObjectTypeEnum.Document,
        ).queryKey;
        queryClient.setQueryData(queryKey, context.previousData);
      }
      toast.error("Failed to delete task. Please try again.");
      rg4js("send", {
        error: normalizeError(error),
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: getCommunicationsForObjectOptions(
          communicationApi,
          pageData.documentId,
          ObjectTypeEnum.Document,
        ).queryKey,
      });
    },
    onMutate: async ({
      communicationId,
      interrogatoryId,
    }: {
      communicationId: string;
      interrogatoryId: number;
    }) => {
      const queryKey = getCommunicationsForObjectOptions(
        communicationApi,
        pageData.documentId,
        ObjectTypeEnum.Document,
      ).queryKey;
      await queryClient.cancelQueries({ queryKey });
      const previousData =
        queryClient.getQueryData<ViewCommunication[]>(queryKey);
      queryClient.setQueryData<ViewCommunication[]>(queryKey, (old) => {
        if (!old) return old;
        return old.map((communication) => {
          if (
            !communication.externalCommunicationItems?.some(
              (item) => item.id === communicationId,
            )
          ) {
            return communication;
          }
          return {
            ...communication,
            externalCommunicationItems:
              communication.externalCommunicationItems?.map((item) => {
                if (item.id !== communicationId) return item;
                return {
                  ...item,
                  responseInfo: {
                    ...item.responseInfo,
                    associatedObjectIds:
                      item.responseInfo?.associatedObjectIds?.filter(
                        (id) => Number(id) !== interrogatoryId,
                      ) ?? [],
                  },
                };
              }),
          };
        });
      });
      return { previousData };
    },
  });

  const updateCommunicationMutation = useMutation({
    mutationFn: async (ids: number[]) => {
      return communicationApi.communicationUpdateCommunication({
        id: pageData.activeCommunication?.id ?? "",
        putClientCommunication: {
          status: CommunicationStatus.Pending,
          communicationInfo: {
            ...pageData.activeCommunication?.communicationInfo,
            requestedReviewIds: ids,
          },
        },
      });
    },
    onError: (error) => {
      rg4js("send", {
        error: normalizeError(error),
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: getCommunicationsForObjectOptions(
          communicationApi,
          pageData.documentId,
          ObjectTypeEnum.Document,
        ).queryKey,
      });
      history.push(
        `/documents/${pageData.documentId}/client-feedback/${pageData.activeCommunication?.id}/confirm-client-info`,
      );
    },
  });

  function saveAndRedirectToReviewFlow() {
    if (pageData.isNextStepBlocked) return;
    const requestedReviewIds =
      pageData.activeCommunication?.externalCommunicationItems
        ?.map((i) =>
          i.responseInfo?.associatedObjectIds?.map((id) => Number(id)),
        )
        .reduce((acc, curr) => acc?.concat(curr ?? []) ?? [], []);
    updateCommunicationMutation.mutate(requestedReviewIds ?? []);
  }

  useEffect(() => {
    if (!pageData.isReviewLocked) return;
    const nextParams = new URLSearchParams(location.search);
    if (nextParams.get("add_task") !== "true") return;
    nextParams.delete("add_task");
    history.replace({
      pathname: location.pathname,
      search: nextParams.toString(),
    });
  }, [history, pageData.isReviewLocked, location.pathname, location.search]);

  function openTaskEditor(communication: ViewCommunicationItem) {
    setEditCommunication(communication);
    searchParams.set("add_task", "true");
    history.push({
      pathname: location.pathname,
      search: searchParams.toString(),
    });
  }

  return {
    // task editor / delete modal selection
    deleteCommunication,
    setDeleteCommunication,
    editCommunication,
    
    // navigation and mutations
    addTask,
    saveAndRedirectToReviewFlow,
    openTaskEditor,
    deleteInterrogatory: deleteInterrogatoryMutation.mutate,
    requestDeleteWithModal: setDeleteCommunication,
    deleteCommunicationItem: deleteCommunicationMutation.mutate,
  };
}

export type ReviewClientTasksPageActionsState = ReturnType<
  typeof useReviewClientTasksPageActions
>;
